import { useEffect, useRef } from 'react';
import { Web } from 'sip.js';
import { sipAorFromAccount } from '@/services/sip/sipAccount.defaults';
import { isSipAccountConfigured, sipAccountConfigError } from '@/services/sip/sipAccount.fromProfile';
import { createSessionManagerDelegate } from './sessionManagerDelegate';
import { REGISTER_PENDING_HINT_MS, insecureContextSipHint, registerRejectMessage } from './sipCallHelpers';
/**
 * Connects WebSocket transport, registers, wires {@link createSessionManagerDelegate}, and cleans up on unmount.
 */
export function useSipWebSessionLifecycle(options) {
    const { accountConfig, sipAudioElement, sipAudioElementRef, mgrRef, subscribeConversationPhaseFnRef, unsubscribeConversationPhaseFnRef, conversationSessionRef, consultSessionRef, warmConsultDialAllowedRef, warmSkipRecoverOnConsultHangupRef, ringTerminatedListenersRef, attachRingTerminatedListener, detachRingTerminatedListener, setSipStatus, setSipError, setIncomingRing, setWarmTransfer, setSipMediaUiEpoch, isOnBreakRef, isOnAcwRef, } = options;
    const onVoiceSessionEndedRef = useRef(options.onVoiceSessionEnded);
    onVoiceSessionEndedRef.current = options.onVoiceSessionEnded;
    const registrationStuckHintTimeoutRef = useRef(undefined);
    useEffect(() => {
        if (!sipAudioElement) {
            return;
        }
        const insecureHint = insecureContextSipHint();
        if (insecureHint) {
            setSipError(insecureHint);
            setSipStatus('error');
            return;
        }
        if (!isSipAccountConfigured(accountConfig)) {
            setSipError(sipAccountConfigError(accountConfig) ?? 'Softphone not assigned to your profile.');
            setSipStatus('idle');
            return;
        }
        const aor = sipAorFromAccount(accountConfig);
        const registrationStuckHintTimeout = typeof window !== 'undefined'
            ? window.setTimeout(() => {
                setSipStatus((s) => {
                    if (s === 'connected') {
                        setSipError((prev) => prev ??
                            'REGISTER did not confirm in time. Desktop SIP uses UDP/TCP; this browser client needs SIP over WebSocket from your PBX (or gateway). Confirm SIP_WS_URL, TLS, and sipDomainHost match your registrar.');
                    }
                    return s;
                });
            }, REGISTER_PENDING_HINT_MS)
            : undefined;
        registrationStuckHintTimeoutRef.current = registrationStuckHintTimeout;
        const registerOptionsWithTimeoutClear = {
            requestDelegate: {
                onReject: (response) => {
                    setSipError(registerRejectMessage(response));
                    setSipStatus('error');
                },
            },
        };
        const sm = new Web.SessionManager(accountConfig.sipWebSocketServerUrl, {
            aor,
            maxSimultaneousSessions: 2,
            iceStopWaitingOnServerReflexive: true,
            registererRegisterOptions: registerOptionsWithTimeoutClear,
            media: {
                constraints: { audio: true, video: false },
                remote: { audio: sipAudioElement },
            },
            userAgentOptions: {
                authorizationUsername: accountConfig.authId,
                authorizationPassword: accountConfig.password,
                displayName: accountConfig.callerId,
                sessionDescriptionHandlerFactoryOptions: {
                    peerConnectionConfiguration: {
                        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
                    },
                },
            },
            delegate: createSessionManagerDelegate({
                mgrRef,
                subscribeConversationPhaseFnRef,
                unsubscribeConversationPhaseFnRef,
                conversationSessionRef,
                consultSessionRef,
                warmConsultDialAllowedRef,
                warmSkipRecoverOnConsultHangupRef,
                ringTerminatedListenersRef,
                detachRingTerminatedListener,
                attachRingTerminatedListener,
                setSipStatus,
                setSipError,
                setIncomingRing,
                setWarmTransfer,
                setSipMediaUiEpoch,
                registrationStuckHintTimeoutRef,
                isOnBreakRef,
                isOnAcwRef,
                onVoiceSessionEnded: () => onVoiceSessionEndedRef.current?.(),
                sipAudioElementRef,
            }),
        });
        mgrRef.current = sm;
        setSipStatus('connecting');
        let cancelled = false;
        sm.connect()
            .then(() => {
            if (cancelled)
                return;
            setSipStatus('connected');
            return sm.register();
        })
            .catch((e) => {
            if (cancelled)
                return;
            const msg = e instanceof Error ? e.message : String(e);
            setSipError(msg);
            setSipStatus('error');
        });
        return () => {
            if (registrationStuckHintTimeoutRef.current !== undefined) {
                window.clearTimeout(registrationStuckHintTimeoutRef.current);
                registrationStuckHintTimeoutRef.current = undefined;
            }
            cancelled = true;
            unsubscribeConversationPhaseFnRef.current();
            mgrRef.current = null;
            conversationSessionRef.current = null;
            consultSessionRef.current = null;
            warmConsultDialAllowedRef.current = false;
            warmSkipRecoverOnConsultHangupRef.current = false;
            setIncomingRing(null);
            ringTerminatedListenersRef.current.forEach((_fn, session) => detachRingTerminatedListener(session));
            void sm
                .unregister()
                .catch(() => undefined)
                .finally(() => sm.disconnect().catch(() => undefined));
        };
    }, [
        accountConfig,
        accountConfig.extension,
        accountConfig.password,
        accountConfig.sipWebSocketServerUrl,
        sipAudioElement,
        sipAudioElementRef,
        mgrRef,
        subscribeConversationPhaseFnRef,
        unsubscribeConversationPhaseFnRef,
        conversationSessionRef,
        consultSessionRef,
        warmConsultDialAllowedRef,
        warmSkipRecoverOnConsultHangupRef,
        ringTerminatedListenersRef,
        attachRingTerminatedListener,
        detachRingTerminatedListener,
        setSipStatus,
        setSipError,
        setIncomingRing,
        setWarmTransfer,
        setSipMediaUiEpoch,
        isOnBreakRef,
        isOnAcwRef,
    ]);
}
